/**
 * CMS API service -- wraps the existing apiClient for all CMS endpoints.
 *
 * Follows the same pattern as the existing resume API calls:
 * - Uses the shared apiClient function (Bearer token, error handling, 204 support)
 * - Groups related endpoints into namespace objects
 */
import { apiClient } from './api.client';
import type {
  CmsSite,
  PersonalInfoBase,
  PersonalInfoOverride,
  CareerEntry,
  CareerEntryVariant,
  Skill,
  Education,
  Certificate,
  Patent,
  CmsTemplate,
  SiteSection,
  SiteCareerEntry,
  SiteSkill,
  SiteEducation,
  SiteCertificate,
  SitePatent,
} from '../types/cms.types';

/* ================================================================== */
/*  Sites                                                              */
/* ================================================================== */

export const cmsSitesApi = {
  list: () => apiClient<CmsSite[]>('/api/v1/sites'),

  get: (id: string) => apiClient<CmsSite>(`/api/v1/sites/${id}`),

  create: (data: Partial<CmsSite>) =>
    apiClient<CmsSite>('/api/v1/sites', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<CmsSite>) =>
    apiClient<CmsSite>(`/api/v1/sites/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiClient<void>(`/api/v1/sites/${id}`, { method: 'DELETE' }),

  /* ---- Sections ---- */

  getSections: (id: string) =>
    apiClient<SiteSection[]>(`/api/v1/sites/${id}/sections`),

  updateSections: (id: string, sections: Partial<SiteSection>[]) =>
    apiClient<SiteSection[]>(`/api/v1/sites/${id}/sections`, {
      method: 'PUT',
      body: JSON.stringify(sections),
    }),

  /* ---- Career entries per site ---- */

  getCareerEntries: (id: string) =>
    apiClient<SiteCareerEntry[]>(`/api/v1/sites/${id}/career-entries`),

  updateCareerEntries: (id: string, entries: Partial<SiteCareerEntry>[]) =>
    apiClient<SiteCareerEntry[]>(`/api/v1/sites/${id}/career-entries`, {
      method: 'PUT',
      body: JSON.stringify(entries),
    }),

  /* ---- Skills per site ---- */

  getSkills: (id: string) =>
    apiClient<SiteSkill[]>(`/api/v1/sites/${id}/skills`),

  updateSkills: (id: string, skills: Partial<SiteSkill>[]) =>
    apiClient<SiteSkill[]>(`/api/v1/sites/${id}/skills`, {
      method: 'PUT',
      body: JSON.stringify(skills),
    }),

  /* ---- Education per site ---- */

  getEducation: (id: string) =>
    apiClient<SiteEducation[]>(`/api/v1/sites/${id}/education`),

  updateEducation: (id: string, education: Partial<SiteEducation>[]) =>
    apiClient<SiteEducation[]>(`/api/v1/sites/${id}/education`, {
      method: 'PUT',
      body: JSON.stringify(education),
    }),

  /* ---- Certificates per site ---- */

  getCertificates: (id: string) =>
    apiClient<SiteCertificate[]>(`/api/v1/sites/${id}/certificates`),

  updateCertificates: (id: string, certs: Partial<SiteCertificate>[]) =>
    apiClient<SiteCertificate[]>(`/api/v1/sites/${id}/certificates`, {
      method: 'PUT',
      body: JSON.stringify(certs),
    }),

  /* ---- Patents per site ---- */

  getPatents: (id: string) =>
    apiClient<SitePatent[]>(`/api/v1/sites/${id}/patents`),

  updatePatents: (id: string, patents: Partial<SitePatent>[]) =>
    apiClient<SitePatent[]>(`/api/v1/sites/${id}/patents`, {
      method: 'PUT',
      body: JSON.stringify(patents),
    }),

  /* ---- Publish ---- */

  publish: (id: string) =>
    apiClient<{ message: string }>(`/api/v1/sites/${id}/publish`, {
      method: 'POST',
    }),
};

/* ================================================================== */
/*  Personal Info                                                      */
/* ================================================================== */

export const personalInfoApi = {
  getBase: () => apiClient<PersonalInfoBase | null>('/api/v1/personal-info'),

  updateBase: (data: Partial<PersonalInfoBase>) =>
    apiClient<PersonalInfoBase>('/api/v1/personal-info', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getOverrides: (siteId: string) =>
    apiClient<PersonalInfoOverride[]>(
      `/api/v1/personal-info/overrides/${siteId}`,
    ),

  upsertOverride: (siteId: string, field_name: string, field_value: string) =>
    apiClient<PersonalInfoOverride>(
      `/api/v1/personal-info/overrides/${siteId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ field_name, field_value }),
      },
    ),

  deleteOverride: (siteId: string, fieldName: string) =>
    apiClient<void>(
      `/api/v1/personal-info/overrides/${siteId}/${fieldName}`,
      { method: 'DELETE' },
    ),
};

/* ================================================================== */
/*  Career Entries (content library)                                   */
/* ================================================================== */

export const careerApi = {
  list: () => apiClient<CareerEntry[]>('/api/v1/career-entries'),

  get: (id: string) => apiClient<CareerEntry>(`/api/v1/career-entries/${id}`),

  create: (data: Partial<CareerEntry>) =>
    apiClient<CareerEntry>('/api/v1/career-entries', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<CareerEntry>) =>
    apiClient<CareerEntry>(`/api/v1/career-entries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiClient<void>(`/api/v1/career-entries/${id}`, { method: 'DELETE' }),

  /* ---- Variants ---- */

  listVariants: (id: string) =>
    apiClient<CareerEntryVariant[]>(`/api/v1/career-entries/${id}/variants`),

  createVariant: (id: string, data: Partial<CareerEntryVariant>) =>
    apiClient<CareerEntryVariant>(`/api/v1/career-entries/${id}/variants`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateVariant: (
    entryId: string,
    variantId: string,
    data: Partial<CareerEntryVariant>,
  ) =>
    apiClient<CareerEntryVariant>(
      `/api/v1/career-entries/${entryId}/variants/${variantId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    ),

  deleteVariant: (entryId: string, variantId: string) =>
    apiClient<void>(
      `/api/v1/career-entries/${entryId}/variants/${variantId}`,
      { method: 'DELETE' },
    ),
};

/* ================================================================== */
/*  Skills (content library)                                           */
/* ================================================================== */

export const skillsApi = {
  list: () => apiClient<Skill[]>('/api/v1/skills'),

  create: (data: Partial<Skill>) =>
    apiClient<Skill>('/api/v1/skills', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Skill>) =>
    apiClient<Skill>(`/api/v1/skills/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiClient<void>(`/api/v1/skills/${id}`, { method: 'DELETE' }),
};

/* ================================================================== */
/*  Education (content library)                                        */
/* ================================================================== */

export const educationApi = {
  list: () => apiClient<Education[]>('/api/v1/education'),

  create: (data: Partial<Education>) =>
    apiClient<Education>('/api/v1/education', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Education>) =>
    apiClient<Education>(`/api/v1/education/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiClient<void>(`/api/v1/education/${id}`, { method: 'DELETE' }),
};

/* ================================================================== */
/*  Certificates (content library)                                     */
/* ================================================================== */

export const certificatesApi = {
  list: () => apiClient<Certificate[]>('/api/v1/certificates'),

  create: (data: Partial<Certificate>) =>
    apiClient<Certificate>('/api/v1/certificates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Certificate>) =>
    apiClient<Certificate>(`/api/v1/certificates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiClient<void>(`/api/v1/certificates/${id}`, { method: 'DELETE' }),
};

/* ================================================================== */
/*  Patents (content library)                                          */
/* ================================================================== */

export const patentsApi = {
  list: () => apiClient<Patent[]>('/api/v1/patents'),

  create: (data: Partial<Patent>) =>
    apiClient<Patent>('/api/v1/patents', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Patent>) =>
    apiClient<Patent>(`/api/v1/patents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiClient<void>(`/api/v1/patents/${id}`, { method: 'DELETE' }),
};

/* ================================================================== */
/*  Templates                                                          */
/* ================================================================== */

export const templatesApi = {
  list: () => apiClient<CmsTemplate[]>('/api/v1/templates'),
};
